import { Col, Row } from 'reactstrap';
import {
  createLookupModel,
  LookupField,
  matchesSearch,
} from 'src/componentes/filtro/search-drawer/searchDrawerUtils';
import { capitalizeFirstLetter } from 'src/utils/string';
import Input from 'src/componentes/input';
import CollapsibleSection from 'src/componentes/CollapsibleSection';
import Checkbox from 'src/componentes/checkbox';
import ColaboradorEGrupoSimplificadoLookup from 'src/lookups/ColaboradorEGrupoSimplificadoLookup';
import OrganizacaoLookup from 'src/lookups/item-organizacao';
import ModeloRiscoLookup from 'src/lookups/modelo-risco';
import MatrizRiscoLookup from 'src/lookups/matriz-risco';
import TipoRiscoLookup from 'src/lookups/tipo-risco';
import StatusItemLookup from 'src/lookups/status-item';
import TagLookup from 'src/lookups/TagLookup';
import ClassificacaoRiscoLookup from 'src/lookups/classificacao-risco';

const RiskSection = ({
  model,
  resources,
  intl,
  configuracao,
  searchQuery,
}) => {
  const fields = [
    { label: intl.formatMessage({ id: 'codigo' }), id: 'codigo' },
    { label: intl.formatMessage({ id: 'nome' }), id: 'nome' },
    { label: resources?.responsaveis, id: 'responsaveis' },
    { label: resources?.areas, id: 'areas' },
    {
      label: intl.formatMessage(
        { id: 'label.considerarAreasSubordinadas' },
        { areas: resources?.areas }
      ),
      id: 'areasSubordinadas',
    },
    { label: intl.formatMessage({ id: 'matrizRisco' }), id: 'matrizRisco' },
    configuracao?.utilizarTipo && {
      label: intl.formatMessage({ id: 'tipoRisco' }),
      id: 'tipoRisco',
    },
    configuracao?.utilizarModelo && {
      label: intl.formatMessage({ id: 'modelo' }),
      id: 'modelo',
    },
    configuracao?.podeVisualizarTag && {
      label: resources.tags,
      id: 'tags',
    },
    configuracao?.utilizarClassificacaoRisco && {
      label: intl.formatMessage({ id: 'label.classificacao' }),
      id: 'classificacoesRisco',
    },
    { label: intl.formatMessage({ id: 'status' }), id: 'status' },
    {
      label: intl.formatMessage({ id: 'desconsiderarStatusConcluido' }),
      id: 'desconsiderarStatusConcluido',
    },
    {
      label: intl.formatMessage({ id: 'desconsiderarStatusCancelado' }),
      id: 'desconsiderarStatusCancelado',
    },
  ].filter(Boolean);

  const matchCount = fields.filter((f) =>
    matchesSearch(f.label, searchQuery)
  ).length;

  const visibleFields = fields.filter((f) =>
    matchesSearch(f.label, searchQuery)
  );

  if (!visibleFields.length && searchQuery) return null;

  const isVisible = (id) => visibleFields.some((f) => f.id === id);

  return (
    <CollapsibleSection
      title={intl.formatMessage({ id: 'labelInformacaoRisco' }, { risco: resources?.risco })}
      forceOpen={matchCount > 0 && searchQuery !== ''}
    >
      <Row>
        {isVisible('codigo') && (
          <Col md={12}>
            <Input model={model.getModel('idIntegracao')} />
          </Col>
        )}

        {isVisible('nome') && (
          <Col md={12}>
            <Input model={model.getModel('nome')} />
          </Col>
        )}

        {isVisible('responsaveis') && (
          <Col md={12}>
            <ColaboradorEGrupoSimplificadoLookup
              abrirEmDrawer
              exibirGrupos={false}
              labelComoTitulo
              multi
              clearable
              model={{
                label: capitalizeFirstLetter(resources?.responsaveis),
                value: model.getModel('responsaveis').value,
                requestChange: model.getModel('responsaveis').requestChange,
                errors: model.getModel('responsaveis').errors,
              }}
            />
          </Col>
        )}

        {isVisible('areas') && (
          <Col md={12}>
            <OrganizacaoLookup
              multi
              clearable
              model={{
                label: capitalizeFirstLetter(resources.areas),
                value: model.getModel('areas').value,
                requestChange: model.getModel('areas').requestChange,
              }}
              data={{ tiposIdSelecionaveis: [1, 2, 3] }}
            />
          </Col>
        )}

        {isVisible('areasSubordinadas') && (
          <Col md={12}>
            <Checkbox
              model={{
                label: intl.formatMessage(
                  { id: 'label.considerarAreasSubordinadas' },
                  { areas: resources.areas }
                ),
                value: model.getModel('areasSubordinadas').value,
                requestChange:
                  model.getModel('areasSubordinadas').requestChange,
              }}
              style={{ marginBottom: 10 }}
            />
          </Col>
        )}

        {isVisible('matrizRisco') && (
          <Col md={12}>
            <LookupField
              Component={MatrizRiscoLookup}
              clearable
              highlight={matchesSearch('matriz', searchQuery)}
              model={createLookupModel(
                intl.formatMessage(
                  { id: 'matrizRisco' },
                  { risco: resources?.risco || 'Risco' }
                ),
                model.getModel('matrizRisco')?.value,
                model.getModel('matrizRisco')?.requestChange
              )}
            />
          </Col>
        )}

        {configuracao?.utilizarTipo && isVisible('tipoRisco') && (
          <Col md={12}>
            <LookupField
              Component={TipoRiscoLookup}
              clearable
              highlight={matchesSearch(resources?.tipoRisco, searchQuery)}
              model={createLookupModel(
                resources?.tipoRisco,
                model.getModel('tipoRisco')?.value,
                model.getModel('tipoRisco')?.requestChange
              )}
            />
          </Col>
        )}

        {configuracao?.utilizarModelo && isVisible('modelo') && (
          <Col md={12}>
            <LookupField
              Component={ModeloRiscoLookup}
              clearable
              highlight={matchesSearch('modelo', searchQuery)}
              model={createLookupModel(
                intl.formatMessage({ id: 'modelo' }),
                model.getModel('modeloRisco')?.value,
                model.getModel('modeloRisco')?.requestChange
              )}
            />
          </Col>
        )}

        {configuracao?.podeVisualizarTag && isVisible('tags') && (
          <Col md={12}>
            <LookupField
              Component={TagLookup}
              multi
              highlight={matchesSearch(
                intl.formatMessage({ id: 'tags' }),
                searchQuery
              )}
              model={createLookupModel(
                resources.tags,
                model.getModel('tags')?.value,
                model.getModel('tags')?.requestChange
              )}
              data={{ funcionalidadesIdSelecionaveis: [7] }}
              podeEditar={false}
            />
          </Col>
        )}

        {configuracao?.utilizarClassificacaoRisco &&
          isVisible('classificacoesRisco') && (
            <Col md={12}>
              <LookupField
                Component={ClassificacaoRiscoLookup}
                multi
                clearable
                highlight={matchesSearch(
                  intl.formatMessage({ id: 'label.classificacao' }),
                  searchQuery
                )}
                model={createLookupModel(
                  intl.formatMessage({ id: 'label.classificacao' }),
                  model.getModel('classificacoesRisco')?.value,
                  model.getModel('classificacoesRisco')?.requestChange
                )}
              />
            </Col>
          )}

        {isVisible('status') && (
          <Col md={12}>
            <LookupField
              Component={StatusItemLookup}
              clearable
              highlight={matchesSearch(
                intl.formatMessage({ id: 'status' }),
                searchQuery
              )}
              model={createLookupModel(
                intl.formatMessage({ id: 'status' }),
                model.getModel('status')?.value,
                model.getModel('status')?.requestChange
              )}
              data={{ idsSelecionados: [1, 2, 3, 4, 5, 7, 8] }}
            />
          </Col>
        )}

        {isVisible('desconsiderarStatusConcluido') && (
          <Col md={12}>
            <Checkbox
              model={createLookupModel(
                intl.formatMessage({ id: 'desconsiderarStatusConcluido' }),
                model.getModel('desconsiderarStatusConcluido')?.value,
                model.getModel('desconsiderarStatusConcluido')?.requestChange
              )}
            />
          </Col>
        )}

        {isVisible('desconsiderarStatusCancelado') && (
          <Col md={12}>
            <Checkbox
              model={createLookupModel(
                intl.formatMessage({ id: 'desconsiderarStatusCancelado' }),
                model.getModel('desconsiderarStatusCancelado')?.value,
                model.getModel('desconsiderarStatusCancelado')?.requestChange
              )}
              style={{ marginTop: 5 }}
            />
          </Col>
        )}
      </Row>
    </CollapsibleSection>
  );
};

export default RiskSection;
